// File created by Reginald Sahil Chand on 27-03-2024.

import React, {type ChangeEvent, useState, useEffect, type ReactElement} from 'react';
import {toast} from 'react-toastify';

import {Button} from '../../../button/Button';

import {type TaskProps} from '../../../../../types/tasks/createTaskTypes';

/**
 * @author Reginald Sahil Chand
 * @email Developer.ReginaldSahilChand@gmail.com
 *
 * @description Returns a boolean true if an error gets reported while creating a task.
 * @summary Shows the error using react-toastify toast.
 *
 * @version 0.1.0
 *
 * @param {task} task - The useState hook, object task which stores the task while creating a task.
 *
 * @returns {boolean | undefined} true | undefined based on if any errors where reported while creating a task.
 */
const handleTaskCreationErrors: (task: TaskProps) => true | undefined = (task: TaskProps) => {
	let hasReportedErrors = false;

	const getTasks = localStorage.getItem('Tasks')!;

	if (getTasks !== null || getTasks !== undefined || getTasks !== 'undefined') {
		const storedTask: TaskProps[] = JSON.parse(getTasks) as TaskProps[];

		if (storedTask[0] !== undefined) {
			const {taskTitle, taskDescription} = storedTask[0];

			if (task.taskTitle === taskTitle) {
				toast.error('Please provide a new title. Entered title already exists.', {
					position: 'top-right',
					autoClose: 5000,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					progress: undefined,
					theme: 'dark',
				});

				hasReportedErrors = true;
				return hasReportedErrors;
			}

			if (task.taskDescription === taskDescription) {
				toast.error('Please provide a new description. Entered description already exists.', {
					position: 'top-right',
					autoClose: 5000,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					progress: undefined,
					theme: 'dark',
				});

				hasReportedErrors = true;
				return hasReportedErrors;
			}
		}
	}

	// ###########################################################################################

	if (task.taskTitle.trim() === '') {
		toast.error('Please provide a valid and meaningful task title.', {
			position: 'top-right',
			autoClose: 5000,
			hideProgressBar: false,
			closeOnClick: true,
			pauseOnHover: true,
			draggable: true,
			progress: undefined,
			theme: 'dark',
		});

		hasReportedErrors = true;
		return hasReportedErrors;
	}

	if (task.taskTitle.length < 5) {
		toast.error('Please ensure that the task title is at least 5 characters long.', {
			position: 'top-right',
			autoClose: 5000,
			hideProgressBar: false,
			closeOnClick: true,
			pauseOnHover: true,
			draggable: true,
			progress: undefined,
			theme: 'dark',
		});

		hasReportedErrors = true;
		return hasReportedErrors;
	}

	if (/^(1|\d)/.test(task.taskTitle)) {
		toast.error('Invalid title format. Start with a letter and avoid symbols.', {
			position: 'top-right',
			autoClose: 5000,
			hideProgressBar: false,
			closeOnClick: true,
			pauseOnHover: true,
			draggable: true,
			progress: undefined,
			theme: 'dark',
		});

		hasReportedErrors = true;
		return hasReportedErrors;
	}

	if (/^[^\w\d\s][^\w\d]*$/.test(task.taskTitle)) {
		toast.error('Invalid title format. Symbols are not allowed. Start with a letter', {
			position: 'top-right',
			autoClose: 5000,
			hideProgressBar: false,
			closeOnClick: true,
			pauseOnHover: true,
			draggable: true,
			progress: undefined,
			theme: 'dark',
		});

		hasReportedErrors = true;
		return hasReportedErrors;
	}

	if (task.taskTitle.length > 50) {
		toast.error(
			'Max title characters reached. Please Ensure your that title is not more than 50 characters long',
			{
				position: 'top-right',
				autoClose: 5000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
				progress: undefined,
				theme: 'dark',
			},
		);

		hasReportedErrors = true;
		return hasReportedErrors;
	}

	// ###########################################################################################

	if (task.taskDescription.trim() === '') {
		toast.error('Please provide a valid and meaningful task description.', {
			position: 'top-right',
			autoClose: 5000,
			hideProgressBar: false,
			closeOnClick: true,
			pauseOnHover: true,
			draggable: true,
			progress: undefined,
			theme: 'dark',
		});

		hasReportedErrors = true;
		return hasReportedErrors;
	}

	if (task.taskDescription.length < 5) {
		toast.error('Please ensure that the task description is at least 5 characters long.', {
			position: 'top-right',
			autoClose: 5000,
			hideProgressBar: false,
			closeOnClick: true,
			pauseOnHover: true,
			draggable: true,
			progress: undefined,
			theme: 'dark',
		});

		hasReportedErrors = true;
		return hasReportedErrors;
	}

	if (/^(1|\d)/.test(task.taskDescription)) {
		toast.error('Invalid description format. Start with a letter and avoid symbols.', {
			position: 'top-right',
			autoClose: 5000,
			hideProgressBar: false,
			closeOnClick: true,
			pauseOnHover: true,
			draggable: true,
			progress: undefined,
			theme: 'dark',
		});

		hasReportedErrors = true;
		return hasReportedErrors;
	}

	if (/^[^\w\d\s][^\w\d]*$/.test(task.taskDescription)) {
		toast.error('Invalid description format. Symbols are not allowed. Start with a letter', {
			position: 'top-right',
			autoClose: 5000,
			hideProgressBar: false,
			closeOnClick: true,
			pauseOnHover: true,
			draggable: true,
			progress: undefined,
			theme: 'dark',
		});

		hasReportedErrors = true;
		return hasReportedErrors;
	}
};

/**
 * @author Reginald Sahil Chand
 * @email Developer.ReginaldSahilChand
 *
 * @description Renders a GUI to create a task which is then saved to the local storage.
 * @version 0.1.0
 *
 * @returns {JSX Element} CreateTask component.
 */
export const CreateTask: React.FC = React.memo((): ReactElement => {
	const [task, setTask] = useState<TaskProps>({
		taskTitle: '',
		taskDescription: '',
	});

	const [tasks, setTasks] = useState<TaskProps[]>(() => {
		let storedTasks: string | undefined = localStorage.getItem('Tasks')!;

		if (storedTasks === undefined || storedTasks === 'undefined') {
			storedTasks = '';
			localStorage.setItem('Tasks', '');
		}

		return storedTasks ? (JSON.parse(storedTasks) as TaskProps[]) : [];
	});

	const getTaskDataOnChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		try {
			const {name, value} = event.target;

			setTask((prevState) => ({
				...prevState,
				[name]: value,
			}));
		} catch (error: unknown) {
			console.error(error);
		}
	};

	const createTask: () => void = () => {
		try {
			const hasReportedErrors = handleTaskCreationErrors(task);

			if (!hasReportedErrors) {
				setTasks((prevTasks) => [...prevTasks, task]);
				setTask({taskTitle: '', taskDescription: ''});

				toast.success('Task successfully created.', {
					position: 'top-right',
					autoClose: 5000,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					progress: undefined,
					theme: 'dark',
				});
			}
		} catch (error: unknown) {
			console.error(error);
		}
	};

	useEffect(() => {
		try {
			localStorage.setItem('Tasks', JSON.stringify(tasks));
		} catch (error) {
			console.error(error);
		}
	}, [tasks]);

	return (
		<form className="w-full h-full md:w-[40%] md:h-[80%] md:absolute md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2">
			<fieldset className="w-full h-full p-5 overflow-y-auto md:rounded-lg bg-primaryBackground">
				<legend
					title="Click To Create A Task"
					aria-label="Create A Task"
					onClick={createTask}
					className="p-5 hidden font-medium text-text cursor-pointer rounded-lg bg-secondary transition-colors duration-300 ease-in-out hover:bg-primary md:block"
				>
					Create a Task
				</legend>
				<label htmlFor="taskTitle" className="my-5 mb-5 flex flex-col text-text">
					Enter the title for your Task
					<input
						type="text"
						name="taskTitle"
						id="taskTitle"
						placeholder="Buy an Orange"
						title="Enter the title for your Task"
						aria-label="Enter the title for your Task"
						value={task.taskTitle}
						onChange={getTaskDataOnChange}
						autoComplete="true"
						required
						className="my-5 p-5 rounded-lg bg-secondaryBackground"
					/>
				</label>

				<label htmlFor="taskDescription" className="my-5 mb-5 flex flex-col text-text">
					Enter the description for your Task
					<textarea
						rows={10}
						name="taskDescription"
						id="taskDescription"
						placeholder="Buy Oranges from Market"
						title="Enter the description for your Task"
						aria-label="Enter the description for your Task"
						value={task.taskDescription}
						onChange={getTaskDataOnChange}
						autoComplete="true"
						className="mt-5 p-5 rounded-lg resize-none bg-secondaryBackground "
					/>
				</label>

				<Button
					buttonWidth="w-[120px]"
					buttonHeight="h-[70px]"
					buttonText="Create Task"
					buttonFunction={createTask}
					buttonDescription="Click To Create A Task"
				/>
			</fieldset>
		</form>
	);
});

CreateTask.displayName = 'CreateTask';
